import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

type TableRowData = Record<string, string | number>;

type DataTableProps = Readonly<{
  data: {
    headers: string[];
    rows: TableRowData[];
  };
}>;

export const DataTable = ({ data }: DataTableProps) => {
  return (
    <TableContainer sx={containerSx}>
      <Table aria-label="data table" size="small" sx={tableSx}>
        <TableHead>
          <TableRow>
            {data.headers.map((header) => (
              <TableCell key={header} sx={headerCellSx}>
                <Typography
                  variant="text-xs-semibold"
                  color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
                >
                  {header}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.rows.map((row, rowIndex) => (
            <TableRow key={`row-${rowIndex}`} sx={rowSx}>
              {data.headers.map((header, headerIndex) => (
                <TableCell key={`${rowIndex}-${headerIndex}`} sx={cellSx}>
                  <Typography
                    variant={headerIndex === 0 ? 'text-sm-medium' : 'text-sm-regular'}
                    color={({ backrs }) =>
                      headerIndex === 0
                        ? backrs.colors['Colors/Text/text-primary']
                        : backrs.colors['Colors/Text/text-tertiary']
                    }
                  >
                    {row[header]}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  boxShadow: backrs.shadows['shadow-xs'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
  width: {
    xs: 'auto',
    sm: 'max-content',
  },
});

const tableSx: SxProps<Theme> = {
  width: 'auto',
};

const headerCellSx: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  paddingY: backrs.spacing['spacing-lg'],
  paddingX: {
    xs: backrs.spacing['spacing-2xl'],
    sm: backrs.spacing['spacing-3xl'],
  },
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  '&:last-child td, &:last-child th': { borderBottom: 0 },
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const cellSx: SxProps<Theme> = ({ backrs }) => ({
  paddingY: backrs.spacing['spacing-xl'],
  paddingX: {
    xs: backrs.spacing['spacing-2xl'],
    sm: backrs.spacing['spacing-3xl'],
  },
});
